import type { ComboboxItemWithValue } from "@replohq/lattice/components/combobox/Combobox";
import type { ImportableThemeBuilderShopifyIntegration } from "schemas/generated/integration";

import React from "react";

import { LabeledControl } from "@replohq/lattice/blocks/labeled-control/LabeledControl";
import {
  Combobox,
  ComboboxTriggerButton,
} from "@replohq/lattice/components/combobox/Combobox";
import { Skeleton } from "@replohq/lattice/components/skeleton/Skeleton";
import ShopifyIcon from "@replohq/lattice/icons/static/shopify-icon";
import { resolveImportShopifyIntegrationId } from "@v2/features/new-shop-onboarding/resolveImportShopifyIntegrationId";

const NONE_VALUE = "none";

const isItemEqualByValue = (
  firstItem: ComboboxItemWithValue,
  secondItem: ComboboxItemWithValue,
) => firstItem.value === secondItem.value;

/**
 * Lets a workspace that already runs a theme-builder Shopify store start its
 * new agent project with that store connected. `undefined` means untouched
 * (default to the first store); `null` is an explicit "None".
 */
export function ImportShopifyIntegrationControl({
  integrations,
  isLoading,
  value,
  onChange,
  disabled,
}: {
  integrations: readonly ImportableThemeBuilderShopifyIntegration[];
  isLoading: boolean;
  value: string | null | undefined;
  onChange: (integrationId: string | null) => void;
  disabled: boolean;
}) {
  const resolvedId = resolveImportShopifyIntegrationId({
    selectedId: value,
    importableIntegrationIds: integrations.map((integration) => integration.id),
  });
  const items: ComboboxItemWithValue[] = [
    { value: NONE_VALUE, label: "None" },
    ...integrations.map((integration) => ({
      value: integration.id,
      label: `${integration.name ?? integration.shopifyUrl} (${integration.shopifyUrl})`,
      startEnhancer: <ShopifyIcon size={14} />,
    })),
  ];
  const selectedItem =
    items.find((item) => item.value === (resolvedId ?? NONE_VALUE)) ??
    items[0] ??
    null;

  return (
    <LabeledControl
      id="import-shopify-integration"
      label="Import Shopify Integration"
      description="Start with a store you've already connected to Replo."
      size="base"
      className="min-w-0"
    >
      {isLoading ? (
        <Skeleton className="h-8 w-full" />
      ) : (
        <Combobox
          items={items}
          value={selectedItem}
          onValueChange={(item) => {
            onChange(item && item.value !== NONE_VALUE ? item.value : null);
          }}
          isItemEqualToValue={isItemEqualByValue}
          disabled={disabled}
          placeholder="Search stores..."
          empty="No stores found"
          trigger={
            <ComboboxTriggerButton
              id="import-shopify-integration"
              size="base"
              startEnhancer={resolvedId ? <ShopifyIcon size={14} /> : undefined}
              isPlaceholder={!resolvedId}
              className="w-full min-w-0 max-w-full"
            >
              {selectedItem?.label}
            </ComboboxTriggerButton>
          }
          triggerAsChild
        />
      )}
    </LabeledControl>
  );
}
